import { useMemo } from 'react';

import { BonusIcon, ITabOption, Label, TTranslator } from '@smartshell/ui-kit';
import cn from 'classnames';
import day from 'dayjs';
import { Column, Row } from 'react-table';

import { PermissionKey } from 'auth/types';
import { TLocalesKeys } from 'localization';
import {
  EPaymentItemType,
  TPayment,
  EPaymentStatus,
  TPaymentItem,
} from 'types';
import { getPaymentMethodLabel } from 'utils';

import { DATE_KEY } from './constants';
import { TTabPayment } from './interfaces';
import s from './Payments.module.scss';
import { RefundButton } from './RefundButton';

const formatSum = (sum: number): string =>
  sum.toLocaleString(undefined, { maximumFractionDigits: 2 });

const shortenValue = (string: string, size = 16): string =>
  string.length < size ? string : `${string.substring(0, size - 3)}...`;

export const useColumns = (
  setClientId: (uuid: string) => void,
  currency: string,
  t: TTranslator<TLocalesKeys>,
  permissions: Set<PermissionKey> | null,
  setNotPermissionModalOpen: (isOpen: boolean) => void,
  size: number
): Array<Column<TPayment>> => {
  return useMemo(() => {
    const columns: Column<TPayment>[] = [
      ...(size > 960
        ? [
            {
              Header: t('payment.number'),
              accessor: 'id' as const,
              minWidth: 138,
            },
          ]
        : []),
      {
        id: DATE_KEY,
        Header: t('payment.date'),
        accessor: ({ created_at }) =>
          created_at ? day(created_at).valueOf() : null,
        Cell: ({ value }: { value: number }) => {
          if (size > 960) {
            return (
              <span title={day(value).format('DD.MM.YYYY HH:mm:ss')}>
                {day(value).format('DD.MM.YYYY HH:mm')}
              </span>
            );
          }
          return (
            <div className={s.date}>
              <span>{day(value).format('DD.MM.YYYY')}</span>
              <span>{day(value).format('HH:mm')}</span>
            </div>
          );
        },
        minWidth: 156,
      },
      ...(size > 960
        ? [
            {
              id: 'category',
              Header: t('category'),
              accessor: ({ items }: { items: Array<TPaymentItem> }) => {
                if (!items) return null;

                for (const currentItem of items) {
                  if (items[0]?.type !== currentItem?.type) {
                    return t('payments.category.multi');
                  }
                }

                switch (items[0]?.type) {
                  case 'TARIFF':
                    return t('payments.category.tariffs');
                  case 'GOOD':
                    return t('payments.category.goods');
                  case 'SERVICE':
                    return t('payments.category.services');
                  case 'DEPOSIT':
                    return t('payments.category.deposit');
                  case EPaymentItemType.BONUS:
                    return t('payments.category.bonuses');
                }

                return null;
              },
              minWidth: 186,
            },
          ]
        : []),

      {
        id: 'client',
        Header: t('client'),
        accessor: ({ client }) =>
          client?.nickname ?? client?.phone ?? t('client.guest'),
        Cell: ({ row, value }: { row: Row<TPayment>; value: string }) => {
          const clientUUID = row.original.client?.uuid;
          return (
            <span
              title={value}
              role="button"
              tabIndex={0}
              data-prevent-row-click={clientUUID ? true : undefined}
              className={cn(s['user'], clientUUID && s['client'])}
              onClick={(e) => {
                e.stopPropagation();
                if (!permissions?.has('client_info')) {
                  setNotPermissionModalOpen(true);
                  return;
                }
                if (clientUUID) setClientId(clientUUID);
              }}
            >
              {shortenValue(value)}
            </span>
          );
        },
        minWidth: 155,
      },
      {
        Header: t('payment'),
        accessor: 'method',
        Cell: ({ value }) => getPaymentMethodLabel(value, t),
        minWidth: 129,
      },
      {
        Header: t('total'),
        accessor: 'sum',
        Cell: ({ value, row }) =>
          row.original.items[0]?.type === EPaymentItemType.BONUS ? (
            <div className={s.bonuses}>
              <BonusIcon size="s" /> <span className={s.sum}>{value}</span>
            </div>
          ) : (
            <div className={s.money}>
              <span className={s.sum}>
                {formatSum(value)} {currency}
              </span>
              {Boolean(row.original.bonus) && (
                <Label
                  iconName={
                    row.original.items?.some(
                      (i) => i.type === EPaymentItemType.DEPOSIT
                    )
                      ? 'BonusAddIcon'
                      : 'BonusRemoveIcon'
                  }
                  size="s"
                  color={
                    row.original.items?.some(
                      (i) => i.type === EPaymentItemType.DEPOSIT
                    )
                      ? 'green'
                      : 'red'
                  }
                  text={String(row.original.bonus)}
                />
              )}
            </div>
          ),
        minWidth: 256,
      },
      ...(size > 960
        ? [
            {
              accessor: 'is_refunded' as const,
              Cell: ({
                value,
                row: { original },
              }: {
                value: boolean;
                row: { original: TPayment };
              }) => {
                if (value)
                  return (
                    <Label
                      color="gray-primary"
                      text={t('payment.refunded')}
                      iconName="CircleCloseIcon"
                      rounded
                    />
                  );

                if (original.status === EPaymentStatus.PENDING)
                  return (
                    <Label
                      color="orange"
                      text={t('payment.status.pending')}
                      rounded
                    />
                  );

                if (original.status === EPaymentStatus.UNPAID)
                  return (
                    <Label
                      color="red"
                      text={t('payment.status.unpaid')}
                      rounded
                    />
                  );

                return permissions?.has('refund_create') &&
                  !original.not_refundable ? (
                  <RefundButton
                    payment={original}
                    disabled={!permissions?.has('refund_create')}
                  />
                ) : null;
              },
              minWidth: 146,
              disableSortBy: true,
              disableResizing: true,
            },
          ]
        : []),
    ];

    return columns;
  }, [setClientId, currency, t, permissions, setNotPermissionModalOpen, size]);
};

export const getTabs = (
  t: TTranslator<TLocalesKeys>
): ITabOption<TTabPayment>[] => [
  {
    value: 'ALL',
    title: t('payments.tab.all'),
    iconName: 'WalletIcon',
  },
  {
    value: 'POST_PAYMENTS',
    title: t('payments.tab.post_payments'),
    iconName: 'TimeForwardIcon',
  },
];
